import { useEffect, useState } from 'react'
import { listAuditLogs } from '~/api/audit'
import { useDashboard } from '~/api/use-stats'
import type { AuditLog } from '~/api/types'
import { ActivityLogSection } from '~/components/dashboard/ActivityLogSection'
import { AttentionPanel } from '~/components/dashboard/AttentionPanel'
import { ChartSection } from '~/components/dashboard/ChartSection'
import { DashboardHeader } from '~/components/dashboard/DashboardHeader'
import { KpiSection } from '~/components/dashboard/KpiSection'
import { type View } from '~/components/dashboard/constants'

// 운영 대시보드: 운영자가 아침에 열어 한눈에 상태 파악 + 바로 행동하는 화면.
// 정보 위계 = 헤더 / 주의 필요(Attention) / 핵심 KPI / 차트(통계·시계열 토글) / 최근 활동.
// 데이터는 GET /admin/api/stats(스냅샷) + /stats/timeseries(추이) + /audit(최근 활동)에서만 받는다.
// 이 컴포넌트는 데이터 로드·상태·레이아웃 오케스트레이션만 담당하고, 각 영역은 dashboard/ 하위로 분리한다.
export function DashboardPage() {
    const [view, setView] = useState<View>('stats')
    const [days, setDays] = useState<number>(30)
    const [autoRefresh, setAutoRefresh] = useState(true)
    const { stats, timeseries, error, lastUpdated, refresh } = useDashboard({
        days,
        autoRefresh,
    })

    // 최근 활동(감사로그 5건) — 기존 GET /admin/api/audit 만 사용.
    const [recent, setRecent] = useState<AuditLog[] | null>(null)
    useEffect(() => {
        let cancelled = false
        listAuditLogs({ limit: 5 })
            .then((rows) => {
                if (!cancelled) setRecent(rows)
            })
            .catch(() => {
                if (!cancelled) setRecent([])
            })
        return () => {
            cancelled = true
        }
    }, [lastUpdated])

    // ← / → 로 통계 ↔ 시계열 보기를 전환한다. 입력 요소 포커스나 수정키(⌘/Ctrl/Alt/Shift)
    // 조합일 때는 무시해 폼 입력·단축키와 충돌하지 않게 한다.
    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
            const el = e.target as HTMLElement | null
            const tag = el?.tagName
            if (
                tag === 'INPUT' ||
                tag === 'TEXTAREA' ||
                tag === 'SELECT' ||
                el?.isContentEditable
            )
                return
            setView(e.key === 'ArrowLeft' ? 'stats' : 'timeseries')
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [])

    // 첫 로딩(스냅샷 미도착) — 에러가 아닌 한 스켈레톤 표시.
    const loading = stats === null && !error

    return (
        <div>
            <DashboardHeader
                view={view}
                onViewChange={setView}
                days={days}
                onDaysChange={setDays}
                autoRefresh={autoRefresh}
                onAutoRefreshChange={setAutoRefresh}
                lastUpdated={lastUpdated}
                onRefresh={refresh}
            />

            {error ? <p className='text-destructive mb-4'>{error}</p> : null}

            {/* 1) 주의 필요(Attention) — 가장 위. 액션 가능한 신호. */}
            <AttentionPanel loading={loading} stats={stats} />

            {/* 2) 핵심 KPI 행 */}
            <KpiSection
                loading={loading}
                view={view}
                stats={stats}
                timeseries={timeseries}
                days={days}
            />

            {/* 3) 차트 영역 — 통계 ↔ 시계열 토글. */}
            <ChartSection
                loading={loading}
                view={view}
                stats={stats}
                timeseries={timeseries}
                days={days}
            />

            {/* 4) 하단 — 최근 활동. */}
            <div className='mt-6'>
                <ActivityLogSection recent={recent} />
            </div>
        </div>
    )
}
